// AnnouncementBanner — shows the currently active platform announcement.
// Fetched on every page load. Super admins see all; regular users see only active ones.
// Dismissal is session-local (does not mark the announcement as inactive).

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/safeSupabase';
import { useAuth } from '@/hooks/useAuth';
import { AlertCircle, Info, AlertTriangle, X } from 'lucide-react';

interface Announcement {
  id: string;
  message: string;
  type: 'info' | 'warning' | 'critical';
  active: boolean;
}

const TYPE_CONFIG = {
  info: {
    icon: Info,
    className: 'bg-blue-50 border-blue-200 text-blue-900 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-100',
    iconClass: 'text-blue-500',
  },
  warning: {
    icon: AlertTriangle,
    className: 'bg-amber-50 border-amber-200 text-amber-900 dark:bg-amber-950 dark:border-amber-800 dark:text-amber-100',
    iconClass: 'text-amber-500',
  },
  critical: {
    icon: AlertCircle,
    className: 'bg-red-50 border-red-200 text-red-900 dark:bg-red-950 dark:border-red-800 dark:text-red-100',
    iconClass: 'text-red-500',
  },
};

export default function AnnouncementBanner() {
  const { user } = useAuth();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const { data: announcements = [] } = useQuery<Announcement[]>({
    queryKey: ['announcements'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('platform_announcements')
        .select('id, message, type, active')
        .eq('active', true)
        .order('created_at', { ascending: false })
        .limit(1);
      if (error) return [];
      return (data ?? []) as Announcement[];
    },
    enabled: !!user,
    staleTime: 60_000,
    refetchInterval: 120_000, // re-check every 2 min
  });

  const visible = announcements.filter(a => !dismissed.has(a.id));
  if (visible.length === 0) return null;

  const announcement = visible[0];
  const config = TYPE_CONFIG[announcement.type] ?? TYPE_CONFIG.info;
  const Icon = config.icon;

  return (
    <div className={`border-b px-4 py-2.5 flex items-center gap-3 text-sm ${config.className}`}>
      <Icon className={`h-4 w-4 shrink-0 ${config.iconClass}`} />
      <p className="flex-1 font-medium">{announcement.message}</p>
      <button
        onClick={() => setDismissed(prev => new Set([...prev, announcement.id]))}
        className="opacity-60 hover:opacity-100 transition-opacity"
        aria-label="Dismiss announcement"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}
